import he from 'he';
import {
  requestViaBackendProxy,
  extractHaitangwMedia,
  buildMediaSourceResult
} from './musicPluginProxy.js';
import {
  enrichKwSearchCovers,
  resolveKwCoverFromItem
} from './plazaCoverUtils.js';

const PAGE_SIZE = 30;
const PLATFORM = 'kw';
const AUDIO_PATH = 'kw_song_kw.php';

const KW_SEARCH_BASE = 'https://search.kuwo.cn/r.s';
const KW_SEARCH_DEFAULTS = {
  client: 'kt',
  uid: '2574100312',
  ver: 'kwplayer_ar_9.2.2.1',
  vipver: '1',
  ft: 'music',
  cluster: '0',
  strategy: '2012',
  encoding: 'utf8',
  rformat: 'json',
  vermerge: '1',
  mobi: '1'
};

const KW_CENGUIGUI_BASE = 'https://kw-api.cenguigui.cn/';

const QUALITY_LEVELS = {
  low: '128',
  standard: '128',
  high: '320',
  super: '320'
};

const CENGUIGUI_LEVELS = {
  low: 'standard',
  standard: 'standard',
  high: 'exhigh',
  super: 'lossless'
};

const SEARCH_HEADERS = {
  Referer: 'https://www.kuwo.cn/',
  'User-Agent':
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36'
};

const REQUEST_HEADERS = {
  Referer: 'https://www.kuwo.cn/',
  'User-Agent':
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36'
};

function normalizeSong(item) {
  if (!item) return null;

  const musicRid = item.MUSICRID || item.musicrid || item.DC_TARGETID || item.id;
  const id = String(musicRid || '').replace(/^MUSIC_/i, '');
  if (!id) return null;

  const artist = he.decode(item.ARTIST || item.artist || item.FARTIST || '未知艺术家');
  const title = he.decode(item.SONGNAME || item.NAME || item.name || item.title || '未知歌曲');
  const album = he.decode(item.ALBUM || item.album || item.albumName || '');
  const cover = resolveKwCoverFromItem(item);

  return {
    id,
    title,
    artist,
    album,
    duration: Number(item.DURATION || item.duration || 0),
    coverUrl: cover,
    artwork: cover,
    source: 'kw-plugin',
    raw: item
  };
}

function buildSearchUrl(query, page) {
  const params = new URLSearchParams({
    ...KW_SEARCH_DEFAULTS,
    all: query.trim(),
    pn: String(page - 1),
    rn: String(PAGE_SIZE)
  });
  return `${KW_SEARCH_BASE}?${params.toString()}`;
}

async function search(query, page = 1) {
  if (!query?.trim()) {
    throw new Error('搜索关键词不能为空');
  }

  const url = buildSearchUrl(query, page);
  const body = await requestViaBackendProxy(url, SEARCH_HEADERS);
  const songs = body?.abslist || body?.data?.list || body?.data?.musicList || [];
  const total = Number(body?.TOTAL ?? body?.total ?? songs.length);
  const data = songs.map(normalizeSong).filter(Boolean);
  await enrichKwSearchCovers(data);

  return {
    isEnd: page * PAGE_SIZE >= total || data.length < PAGE_SIZE,
    total,
    data
  };
}

async function getMediaSourceFromCenguigui(songId, quality, musicItem) {
  const level = CENGUIGUI_LEVELS[quality] || CENGUIGUI_LEVELS.high;
  const url = `${KW_CENGUIGUI_BASE}?id=${encodeURIComponent(songId)}&type=song&level=${level}&format=json`;
  const responseData = await requestViaBackendProxy(url, REQUEST_HEADERS);

  if (responseData?.code !== undefined && Number(responseData.code) !== 200) {
    throw new Error(responseData.msg || '酷我播放接口失败');
  }

  const audioData = responseData?.data;
  if (!audioData?.url) {
    throw new Error('无法获取酷我音频URL');
  }

  return buildMediaSourceResult(audioData, musicItem, quality);
}

async function getMediaSourceFromHaitangw(songId, quality, musicItem) {
  const qualityLevel = QUALITY_LEVELS[quality] || QUALITY_LEVELS.high;
  const audioUrl = `https://music.haitangw.cc/music/${AUDIO_PATH}?id=${songId}&quality=${qualityLevel}`;
  const responseData = await requestViaBackendProxy(audioUrl, REQUEST_HEADERS);
  const audioData = extractHaitangwMedia(responseData);
  return buildMediaSourceResult(audioData, musicItem, quality);
}

async function getMediaSource(musicItem, quality = 'high') {
  const songId = musicItem?.id || musicItem?.raw?.rid || musicItem?.raw?.id;
  if (!songId) {
    throw new Error('酷我歌曲ID不能为空');
  }

  try {
    return await getMediaSourceFromHaitangw(songId, quality, musicItem);
  } catch (haitangError) {
    console.warn('[kwPluginClient] 海棠网获取失败，回退 cenguigui:', haitangError.message);
    return getMediaSourceFromCenguigui(songId, quality, musicItem);
  }
}

/**
 * 保存到网盘时优先走 cenguigui JSON 接口拿 CDN 直链，避免海棠网慢响应拖住整次保存。
 * cenguigui 与海棠网并行请求，谁先返回有效 URL 用谁（单次最多等 5s）。
 */
async function getMediaSourceForSave(musicItem, quality = 'high') {
  const songId = musicItem?.id || musicItem?.raw?.rid || musicItem?.raw?.id;
  if (!songId) {
    throw new Error('酷我歌曲ID不能为空');
  }

  const withAttemptTimeout = (promise, ms = 5000) => {
    let timeoutId;
    return Promise.race([
      promise,
      new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error('接口超时')), ms);
      })
    ]).finally(() => {
      if (timeoutId) clearTimeout(timeoutId);
    });
  };

  const results = await Promise.allSettled([
    withAttemptTimeout(getMediaSourceFromCenguigui(songId, quality, musicItem)),
    withAttemptTimeout(getMediaSourceFromHaitangw(songId, quality, musicItem))
  ]);

  for (const result of results) {
    if (result.status === 'fulfilled' && result.value?.url) {
      return result.value;
    }
  }

  const errors = results
    .filter((item) => item.status === 'rejected')
    .map((item) => item.reason?.message || String(item.reason))
    .join('; ');
  throw new Error(errors || '酷我 CDN 解析失败');
}

async function getLyric(musicItem, options = {}) {
  if (options.cachedLyric) {
    return options.cachedLyric;
  }
  return getMediaSource(musicItem, 'high').then((result) => {
    if (result.lyric) {
      return result.lyric;
    }
    throw new Error('无法获取酷我歌词');
  });
}

const kwPluginClient = {
  platform: PLATFORM,
  version: '1.2.0',
  search,
  getMediaSource,
  getMediaSourceForSave,
  getLyric
};

export default kwPluginClient;
